'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

interface CounterProps {
  target: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  duration?: number;
}

function AnimatedCounter({
  target,
  suffix = '',
  prefix = '',
  decimals = 0,
  duration = 2200,
}: CounterProps) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, target, duration, decimals]);

  return (
    <span ref={ref}>
      {prefix}
      {decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString()}
      {suffix}
    </span>
  );
}

export default function HeroSection() {
  const mouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      if (blob1Ref.current) {
        const dx = mouseRef.current.x / window.innerWidth - 0.5;
        const dy = mouseRef.current.y / window.innerHeight - 0.5;
        blob1Ref.current.style.transform = `translate(${dx * 30}px, ${dy * 20}px) scale(1)`;
      }
      if (blob2Ref.current) {
        const dx = mouseRef.current.x / window.innerWidth - 0.5;
        const dy = mouseRef.current.y / window.innerHeight - 0.5;
        blob2Ref.current.style.transform = `translate(${-dx * 20}px, ${-dy * 15}px) scale(1)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden hero-gradient"
      aria-label="Hero section"
    >
      {/* ── Depth Layer 1: Background gradient mesh ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 20% 80%, rgba(22,163,74,0.12) 0%, transparent 60%), radial-gradient(ellipse 50% 50% at 80% 20%, rgba(15,45,24,0.4) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      {/* ── Depth Layer 2: Animated blobs ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          ref={blob1Ref}
          className="blob-animate absolute"
          style={{
            width: '600px',
            height: '600px',
            top: '-100px',
            right: '-100px',
            background: 'radial-gradient(circle, rgba(22,163,74,0.15) 0%, transparent 70%)',
            transition: 'transform 0.8s cubic-bezier(0.25, 1, 0.5, 1)',
          }}
        />

        <div
          ref={blob2Ref}
          className="blob-animate-slow absolute"
          style={{
            width: '800px',
            height: '800px',
            bottom: '-200px',
            left: '-200px',
            background: 'radial-gradient(circle, rgba(22,163,74,0.08) 0%, transparent 70%)',
            transition: 'transform 0.8s cubic-bezier(0.25, 1, 0.5, 1)',
          }}
        />
      </div>

      {/* ── Depth Layer 3: Grid pattern ── */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(22,163,74,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(22,163,74,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
        aria-hidden="true"
      />

      {/* ── Hero image (cinematic overlay) ── */}
      <div className="absolute inset-0 pointer-events-none z-[1]" aria-hidden="true">
        <Image
          src="/assets/images/batisheroimg.png"
          alt="Industrial poultry production facility with rows of hens in climate-controlled housing"
          fill
          priority
          className="object-cover opacity-[0.09]"
          sizes="100vw"
        />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left: Main headline */}
          <div className="lg:col-span-7 space-y-8">
            {/* Label */}
            <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <span className="section-label flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-primary dot-pulse inline-block" />
                Batis Poultry Kenya · Protein Infrastructure
              </span>
            </div>

            {/* Headline */}
            <div className="animate-clip-reveal" style={{ animationDelay: '0.4s' }}>
              <h1 className="font-display text-[clamp(3rem,8vw,7rem)] leading-[0.88] tracking-[-0.04em] text-white">
                Africa&apos;s Egg
                <br />
                <span className="text-gradient-green">Supply Engine.</span>
              </h1>
            </div>

            {/* Subheadline */}
            <div className="animate-fade-up" style={{ animationDelay: '0.7s' }}>
              <p className="text-lg lg:text-xl text-white/55 font-medium leading-relaxed max-w-xl">
                Industrial-scale production solving a{' '}
                <span className="text-yellow-400 font-bold">2.4 billion egg deficit</span> and
                stabilizing Kenya&apos;s protein supply chain.
              </p>
            </div>

            {/* CTAs */}
            <div
              className="animate-fade-up flex flex-wrap gap-4"
              style={{ animationDelay: '0.9s' }}
            >
              <a
                href="#invest"
                className="btn-yellow px-7 py-3.5 rounded-full text-base font-bold inline-flex items-center gap-2"
              >
                Request Investment Materials
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a
                href="#solution"
                className="btn-outline-green px-7 py-3.5 rounded-full text-base inline-flex items-center gap-2"
              >
                View Business Model
              </a>
            </div>
          </div>

          {/* Right: Stat cards */}
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: '1.1s' }}>
            <div className="grid grid-cols-2 gap-3">
              {/* Stat 1 */}
              <div className="glass-dark rounded-2xl p-5 col-span-2">
                <p className="text-xs text-white/70 font-mono-custom uppercase tracking-widest mb-2">
                  Annual Egg Deficit · Kenya
                </p>
                <div className="metric-number text-[3.5rem] text-yellow-accent">
                  <AnimatedCounter target={2.4} suffix="B" decimals={1} />
                </div>
                <p className="text-sm text-white/70 mt-3">eggs not produced annually</p>
              </div>

              {/* Stat 2 */}
              <div className="glass-green rounded-2xl p-5">
                <p className="text-xs text-white/70 font-mono-custom uppercase tracking-widest mb-2">
                  Annual Demand
                </p>
                <div className="metric-number text-[2rem]">
                  <AnimatedCounter target={9} suffix="B" />
                </div>
                <p className="text-xs text-white/70 mt-3">eggs/year</p>
              </div>

              {/* Stat 3 */}
              <div className="glass-green rounded-2xl p-5">
                <p className="text-xs text-white/70 font-mono-custom uppercase tracking-widest mb-2">
                  Current Supply
                </p>
                <div className="metric-number text-[2rem]">
                  <AnimatedCounter target={6.6} suffix="B" decimals={1} />
                </div>
                <p className="text-xs text-white/70 mt-3">eggs/year</p>
              </div>

              {/* Stat 4 */}
              <div className="glass-dark rounded-2xl p-5">
                <p className="text-xs text-white/70 font-mono-custom uppercase tracking-widest mb-2">
                  Market Growth
                </p>
                <div className="metric-number text-[2rem]">
                  <AnimatedCounter target={10} suffix="%" />
                </div>
                <p className="text-xs text-white/70 mt-3">annually</p>
              </div>

              {/* Stat 5 */}
              <div className="glass-dark rounded-2xl p-5">
                <p className="text-xs text-white/70 font-mono-custom uppercase tracking-widest mb-2">
                  Import Cost
                </p>
                <div className="metric-number text-[2rem]">
                  $<AnimatedCounter target={58} suffix="M+" />
                </div>
                <p className="text-xs text-white/70 mt-3">annually</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom scroll indicator */}
        <div
          className="mt-20 flex items-center gap-4 animate-fade-up"
          style={{ animationDelay: '1.4s' }}
        >
          <div className="divider-green flex-1 max-w-[120px]" />
          <span className="text-xs text-white/70 font-mono-custom uppercase tracking-widest">
            Scroll to explore
          </span>
          <div className="divider-green flex-1 max-w-[120px]" />
        </div>
      </div>
    </section>
  );
}
